import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare, User, Settings, LogOut, Trash2, Search, Edit, Check, X, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import { updateThreadTitle, deleteThread } from '../../services/api';
import Button from "../other/Button";
import Input from "../other/Input";
import ConfirmDialog from "../other/ConfirmDialog";
import ProfileModal from "../other/ProfileModal";
import SettingsModal from "../other/SettingsModal";
import Avatar from "../other/Avatar";

const Sidebar = ({
  user,
  conversations,
  setConversations,
  currentConversation,
  onCreateNewChat,
  onSetCurrentConversation,
  refreshConversations,
  isLoading = false,
  isThreadsLoading = false,
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
  darkMode = true,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const editInputRef = useRef(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const filteredConversations = conversations.filter(conv =>
    conv.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.thread_id?.includes(searchQuery)
  );
  const formatConversationTime = (dateString) => {
    if (!dateString) return "Invalid";
    const safeString = (!dateString.endsWith('Z') && !dateString.includes('+'))
      ? dateString + 'Z'
      : dateString;
    const date = new Date(safeString);
    if (isNaN(date.getTime())) return "Invalid";

    const now = new Date();

    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMinutes < 60) {
      return `${diffMinutes} min ago`;
    }

    if (diffHours < 24) {
      return `${diffHours} hr ago`;
    }

    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (date >= startOfYesterday && date < startOfToday) {
      return "Yesterday";
    }

    return date.toLocaleDateString([], {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };


  const formatDate = (dateString) => {
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };
  const groupConversationsByDate = (conversations) => {
    const today = new Date();

    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    const startOfLast7Days = new Date(startOfToday);
    startOfLast7Days.setDate(startOfLast7Days.getDate() - 7);

    return conversations.reduce(
      (groups, conv) => {
        const date = new Date(
          conv.created_at || conv.timestamp
        );

        if (date >= startOfToday) {
          groups.today.push(conv);
        } else if (date >= startOfYesterday) {
          groups.yesterday.push(conv);
        } else if (date >= startOfLast7Days) {
          groups.last7Days.push(conv);
        } else {
          groups.older.push(conv);
        }

        return groups;
      },
      {
        today: [],
        yesterday: [],
        last7Days: [],
        older: [],
      }
    );
  };
  const groupedConversations =
    groupConversationsByDate(filteredConversations);
  const handleLogout = () => {
    localStorage.removeItem('user');
    // replace, not href: don't leave the conversation stack behind /login —
    // otherwise Back after a fresh login would resurface pre-logout chats.
    window.location.replace('/login');
  };

  const handleStartEditing = (conv) => {
    setEditingThreadId(conv.thread_id);
    setNewTitle(conv.title || '');
  };

  const handleCancelEditing = () => {
    setEditingThreadId(null);
    setNewTitle('');
  };


  const handleSaveTitle = async (threadId) => {
    if (!newTitle.trim() || !threadId) return;

    const originalConversations = [...conversations];
    const newTitleStr = newTitle.trim();

    // Optimistic UI Update
    setConversations(prev =>
      prev.map(c => c.thread_id === threadId ? { ...c, title: newTitleStr } : c)
    );

    handleCancelEditing();

    try {
      await updateThreadTitle(threadId, newTitleStr, user.user_id);
    } catch (error) {
      console.error("Failed to update title:", error);
      // If the API call fails, revert to the original state
      setConversations(originalConversations);
    }
  };

  const handleDeleteConversation = (conv) => {
    setDeleteTarget(conv);
  };

  const confirmDeleteConversation = async () => {
    if (!deleteTarget) return;
    const threadId = deleteTarget.thread_id;
    const originalConversations = [...conversations];

    setIsDeleting(true);
    try {
      await deleteThread(threadId, user.user_id);
      // Update UI only after a successful delete
      setConversations(prev => prev.filter(c => c.thread_id !== threadId));
      if (currentConversation === threadId) {
        onSetCurrentConversation(null);
      }
      setDeleteTarget(null);
    } catch (error) {
      console.error("Failed to delete conversation:", error);
      setConversations(originalConversations);
    } finally {
      setIsDeleting(false);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);
  useEffect(() => {
    if (editingThreadId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingThreadId]);
  const SidebarSkeleton = () => (
    <div className="px-4 py-3 space-y-5 animate-pulse">
      {[1, 2, 3, 4].map((item) => (
        <div
          key={item}
          className="flex items-start gap-3"
        >
          <div className="w-10 h-10 rounded-full skeleton-blue" />

          <div className="flex-1 space-y-2">
            <div className="h-3 w-full rounded-full skeleton-blue" />
            <div className="h-3 w-2/3 rounded-full skeleton-blue" />
          </div>
        </div>
      ))}
    </div>
  );


  return (
    <div className={`
hidden md:flex
flex-col
${darkMode
        ? "glass-panel"
        : "bg-slate-50 border-r border-slate-300 shadow-lg"
      }
transition-all duration-300
${collapsed ? "w-[76px]" : "w-[300px]"}
`}
    >
      {/* Header */}
      <div className={`p-3 border-b ${darkMode
        ? "hairline"
        : "border-slate-200"
        }`}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2.5">
            {/* Brand monogram */}
            <div
              className={`relative w-10 h-10 rounded-xl flex items-center justify-center ${darkMode ? "glass-sheen glass" : "bg-white border border-slate-200 shadow-sm"}`}
              title="Cloud Ambassadors"
            >
              <span className={`text-lg font-extrabold leading-none ${darkMode ? "text-gradient-accent" : "text-sky-700"}`}>
                CA
              </span>
            </div>

            {/* Expand toggle */}
            <Button darkMode={darkMode}
              variant="icon"
              size="icon"
              onClick={() => setCollapsed(false)}
              title="Expand sidebar"
            >
              <PanelLeftOpen className="w-5 h-5" />
            </Button>

            {/* New Chat */}
            <Button darkMode={darkMode}
              onClick={onCreateNewChat}
              variant="primary"
              size="icon"
              className="rounded-xl shrink-0 shadow-sm hover:shadow-md"
              title="New chat"
            >
              <Edit className="w-5 h-5" />
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 px-1">
              <img
                src="/images/Cloud-Ambassadors-logo.png"
                alt="Logo"
                className="h-11 w-auto"
              />
              <Button darkMode={darkMode}
                variant="icon"
                size="icon"
                onClick={() => setCollapsed(true)}
                title="Collapse sidebar"
              >
                <PanelLeftClose className="w-5 h-5" />
              </Button>
            </div>
            <a
              href="/chat"
              onClick={(e) => {
                e.preventDefault();
                onCreateNewChat();
              }}
              className={`w-full h-[52px] rounded-2xl flex items-center justify-center gap-2 border font-medium transition-all ${darkMode
                  ? "bg-accent-500 text-white border-accent-500 hover:bg-accent-400"
                  : "bg-[#BAE6FD] text-[#075985] border-[#38BDF8] hover:bg-[#BAE6FD]"
                }`}
            >
              <Edit className="w-5 h-5" />
              <span>New Chat</span>
            </a>
          </>
        )}
      </div>

      {/* Search */}
      {!collapsed && (
        <div className="p-4">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode
              ? "text-slate-400"
              : "text-slate-800"
              }`} />
            <Input
              darkMode={darkMode}
              type="text"
              ref={editInputRef}
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 transition-all duration-300 focus:scale-[1.01] ${darkMode ? "" : "text-slate-900 placeholder:text-slate-500"
                }`}
            />
          </div>
        </div>
      )}

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto scrollbar-transparent">
        {!collapsed && (
          <div className="px-4 pb-2">
            <div className={`text-xs font-semibold uppercase tracking-wider ${darkMode
              ? "text-slate-400"
              : "text-slate-800"
              }`}>
              Chats
            </div>
          </div>
        )}

        {isThreadsLoading && conversations.length === 0 ? (
          <SidebarSkeleton />
        ) : (
          <div className="space-y-4 px-2">
            {filteredConversations.length === 0 ? (
              <div className={`flex flex-col items-center justify-center ${collapsed ? "py-4 px-1" : "py-10 px-4 text-center"}`}>
                <div
                  title="No conversations yet"
                  className={`flex flex-col items-center ${collapsed ? "opacity-50 hover:opacity-100 transition-opacity" : ""}`}
                >
                  <MessageSquare className={`${collapsed ? "w-6 h-6" : "w-12 h-12 mb-4"} ${darkMode ? "text-slate-500" : "text-slate-400"}`} />
                </div>
                {!collapsed && (
                  <>
                    <p className={`text-sm mb-2 font-medium ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                      {searchQuery ? 'No conversations found' : 'No conversations yet'}
                    </p>
                    {!searchQuery && (
                      <p className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-400"}`}>
                        Start a new chat to begin
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                {[
                  ['Today', groupedConversations.today],
                  ['Yesterday', groupedConversations.yesterday],
                  ['Last 7 Days', groupedConversations.last7Days],
                  ['Older', groupedConversations.older],
                ].map(([sectionTitle, items]) =>
                  items.length > 0 ? (
                    <div key={sectionTitle}>
                      {!collapsed && (
                        <div className="px-4 pt-3 mb-2">
                          <h3
                            className={`text-[11px] font-semibold uppercase tracking-[0.15em] ${darkMode
                              ? "text-slate-400"
                              : "text-slate-800"}`}
                          >
                            {sectionTitle}
                          </h3>
                        </div>
                      )}

                      <div className="space-y-1">
                        {items.map((conv) => (
                          <div
                            key={conv.thread_id}
                            className={`w-full text-left group relative mx-1 rounded-xl
                                transition-all duration-300 ease-out
                                ${collapsed
                                ? ""
                                : currentConversation === conv.thread_id
                                  ? darkMode
                                    ? "bg-accent-400/10 border border-accent-400/30 shadow-glow-sm"
                                    : "bg-sky-50 border border-sky-300 shadow-sm"
                                  : darkMode
                                    ? "border border-transparent hover:bg-white/5"
                                    : "border border-transparent hover:bg-slate-100"
                              }`}
                          >
                            <div
                              className={`
                                py-3 transition-[padding] duration-300 ease-out
                                ${collapsed ? "flex justify-center px-0" : "pl-4 pr-4 group-hover:pr-20"}
                              `}
                            >
                              {editingThreadId === conv.thread_id ? (
                                <div className="flex items-center space-x-2">
                                  <Input
                                    darkMode={darkMode}
                                    ref={editInputRef}
                                    type="text"
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                        handleSaveTitle(conv.thread_id);
                                      if (e.key === "Escape")
                                        handleCancelEditing();
                                    }}
                                    className={`text-sm py-1 ${darkMode ? "bg-slate-700 text-white" : "bg-white border-sky-300 text-slate-900 shadow-sm"}`}
                                  />

                                  <button
                                    onClick={() =>
                                      handleSaveTitle(conv.thread_id)
                                    }
                                    className="text-green-400 hover:text-green-300"
                                  >
                                    <Check size={18} />
                                  </button>
                                  <button
                                    onClick={handleCancelEditing}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <X size={18} />
                                  </button>
                                </div>
                              ) : (

                                <div
                                  onClick={() =>
                                    onSetCurrentConversation(conv.thread_id)
                                  }
                                  className="cursor-pointer"
                                  title={
                                    collapsed
                                      ? conv.title ||
                                      `Conversation ${conv.thread_id?.slice(-4)}`
                                      : undefined
                                  }
                                >
                                  {collapsed ? (
                                    <div
                                      className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold border transition-all duration-200 ${currentConversation === conv.thread_id
                                        ? darkMode
                                          ? "bg-accent-gradient text-[#04121F] border-transparent shadow-glow-sm"
                                          : "bg-sky-200 text-sky-900 border-sky-400 shadow-sm"
                                        : darkMode
                                          ? "glass text-slate-300 border-white/10 group-hover:text-white group-hover:border-white/20"
                                          : "bg-white text-slate-600 border-slate-300 shadow-sm group-hover:text-slate-900 group-hover:border-slate-400"
                                        }`}
                                    >
                                      {(conv.title || "C").trim().charAt(0).toUpperCase() || "C"}
                                    </div>
                                  ) : (
                                    <>
                                      <p
                                        className={`text-sm font-medium truncate transition-colors duration-200 ${currentConversation === conv.thread_id
                                          ? darkMode
                                            ? "text-white"
                                            : "text-slate-900"
                                          : darkMode
                                            ? "text-slate-300"
                                            : "text-slate-700"
                                          }`}
                                      >
                                        {conv.title ||
                                          `Conversation ${conv.thread_id?.slice(-4)}`}
                                      </p>

                                      <p
                                        className={`text-xs mt-1 ${darkMode
                                          ? "text-slate-400"
                                          : "text-slate-800"
                                          }`}
                                      >
                                        {formatConversationTime(
                                          conv.created_at || conv.timestamp
                                        )}
                                      </p>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            {!collapsed &&
                              editingThreadId !== conv.thread_id && (
                                <div className="
                                    absolute
                                    right-2
                                    top-1/2
                                    -translate-y-1/2
                                    flex
                                    items-center
                                    space-x-1
                                    opacity-0
                                    translate-x-2
                                    group-hover:opacity-100
                                    group-hover:translate-x-0
                                    transition-all
                                    duration-300
                                    ease-out
                                    "
                                >
                                  <Button darkMode={darkMode}
                                    variant="icon"
                                    size="xs"
                                    onClick={() => handleStartEditing(conv)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button darkMode={darkMode}
                                    variant="icon"
                                    size="xs"
                                    onClick={() =>
                                      handleDeleteConversation(conv)
                                    }
                                    className="hover:text-red-400"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* Footer */}
      <div
        className="relative border-t hairline p-3"
        ref={profileMenuRef}
      >
        {/* Floating Menu */}
        {showProfileMenu && (
          <div
            className={`
        absolute
        bottom-full
        left-3
        w-[220px]
        mb-3
        rounded-2xl
        overflow-hidden
        shadow-e3
        z-[999]
        ${darkMode ? "glass-strong" : "bg-white border border-slate-200 shadow-lg"}
      `}
          >
            <button
              onClick={() => {
                setShowProfile(true);
                setShowProfileMenu(false);
              }}
              className={`
          w-full
          flex items-center gap-3
          px-4 py-2.5
          text-[14px]
          transition-colors
          ${darkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"}
        `}
            >
              <User className={`w-4 h-4 ${darkMode ? "text-slate-400" : "text-slate-500"}`} />
              <span>Profile</span>
            </button>

            <button
              onClick={() => {
                setShowSettings(true);
                setShowProfileMenu(false);
              }}
              className={`
          w-full
          flex items-center gap-3
          px-4 py-2.5
          text-[14px]
          transition-colors
          ${darkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"}
        `}
            >
              <Settings className={`w-4 h-4 ${darkMode ? "text-slate-400" : "text-slate-500"}`} />
              <span>Settings</span>
            </button>

            <div className={`border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`} />

            <button
              onClick={handleLogout}
              className={`
          w-full
          flex items-center gap-3
          px-4 py-2.5
          text-[14px]
          transition-colors
          ${darkMode ? "text-red-400 hover:bg-red-500/10" : "text-red-600 hover:bg-red-50"}
        `}
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        )}

        {/* Profile Row — the whole row highlights on hover and opens the menu on click */}
        <button
          type="button"
          onClick={() => setShowProfileMenu((prev) => !prev)}
          aria-expanded={showProfileMenu}
          className={`flex items-center w-full rounded-2xl p-2 transition-colors duration-200 ${collapsed ? "justify-center" : "gap-3"
            } ${darkMode ? "hover:bg-white/5" : "hover:bg-slate-100"}`}
        >
          <div
            className="
        w-10 h-10
        rounded-2xl
        overflow-hidden
        shrink-0
      "
          >
            <Avatar user={user} className="w-full h-full rounded-2xl" />
          </div>

          {/* User Info */}
          {!collapsed && (
            <div className="min-w-0 flex-1 text-left">
              <p
                className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-slate-900"
                  }`}
              >
                {user?.name || "User"}
              </p>

              <p
                className={`text-xs truncate ${darkMode ? "text-slate-400" : "text-slate-500"
                  }`}
              >
                {user?.email}
              </p>
            </div>
          )}
        </button>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        tone="danger"
        title="Delete conversation?"
        message={
          <>
            This will permanently delete{" "}
            <span className={`font-medium ${darkMode ? "text-slate-200" : "text-slate-800"}`}>
              “{deleteTarget?.title || "New Conversation"}”
            </span>
            . This action can’t be undone.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={isDeleting}
        onConfirm={confirmDeleteConversation}
        onCancel={() => !isDeleting && setDeleteTarget(null)}
        darkMode={darkMode}
      />

      <ProfileModal
        open={showProfile}
        onClose={() => setShowProfile(false)}
        user={user}
        onSignOut={handleLogout}
        darkMode={darkMode}
      />

      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        user={user}
        darkMode={darkMode}
      />
    </div>
  );
};

export default Sidebar;
